import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { useDispatch } from "react-redux";
import { snackbarStart } from "../../store/SnackbarSlice";
import { start, done } from "../../store/loaderSlice";
import { Upload, FileSpreadsheet, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { REGISTER_BATCH_URL } from "../../constant";

export default function CreateUserFromExcel({ token, getData, onClose }) {
  const [loading, setLoading] = useState(false);
  const [usersData, setUsersData] = useState([]);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const sendData = async () => {
    dispatch(start());
    try {
      const { data } = await axios({
        method: "post",
        url: REGISTER_BATCH_URL,
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": token,
        },
        data: { users: usersData },
      });
      dispatch(
        snackbarStart({
          text: t("createUserFromExcel.successMessage"),
          severity: "success",
        }),
      );
      getData();
    } catch (err) {
      console.log(err);
      dispatch(
        snackbarStart({
          text: err?.response?.data || t("createUserFromExcel.smthWentWrong"),
          severity: "error",
        }),
      );
    }

    dispatch(done());
    if (onClose) onClose();
  };

  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const workbook = XLSX.read(bstr, { type: "binary" });
      const sheetName = workbook.SheetNames[0]; // first sheet
      const worksheet = workbook.Sheets[sheetName];
      const excelData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      // Transform excel rows into required objects
      const formattedData = excelData.map((array) => ({
        name: array[0],
        email: `${array[1]}`,
        password: `${array[2]}`,
        isAdmin: false,
        status: "Active",
        availableTests: array[3]
          ? array[3]
              .toString()
              .split(",")
              .map((id) => id.trim())
              .filter((id) => id !== "")
          : [],
      }));
      setUsersData(formattedData);
    };
    reader.readAsBinaryString(selectedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload({ target: { files: e.dataTransfer.files } });
      e.dataTransfer.clearData();
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto bg-white dark:bg-[#0f172a] rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-8 transition-colors duration-300">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
          {t("createUserFromExcel.title")}
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            title={t("createUserFromExcel.cancelBtn")}
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="mb-8">
        <div
          className="relative group"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            ref={fileInputRef}
            className="hidden"
          />
          <div
            className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200 ${
              file
                ? "border-blue-500 bg-blue-50/50 dark:bg-blue-500/10"
                : "border-slate-300 dark:border-slate-700 hover:border-blue-500 dark:hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 bg-slate-50/50 dark:bg-slate-900/50"
            }`}
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center px-4 pointer-events-none">
              {file ? (
                <>
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full text-blue-600 dark:text-blue-400 mb-3 shadow-sm">
                    <FileSpreadsheet className="w-8 h-8" />
                  </div>
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[250px] mb-1">
                    {file.name}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                    {t("createUserFromExcel.readyToUpload")}{" "}
                    {usersData.length > 0 &&
                      `(${usersData.length} ${t("createUserFromExcel.usersFound")})`}
                  </p>
                </>
              ) : (
                <>
                  <div className="p-3 bg-slate-200 dark:bg-slate-800 rounded-full text-slate-500 dark:text-slate-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors mb-3 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 shadow-sm">
                    <Upload className="w-8 h-8" />
                  </div>
                  <p className="mb-2 text-sm text-slate-700 dark:text-slate-300">
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {t("createUserFromExcel.clickToUpload")}
                    </span>{" "}
                    {t("createUserFromExcel.orDragAndDrop")}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t("createUserFromExcel.excelFilesOnly")}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg">
          <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-400 mb-2">
            {t("createUserFromExcel.requiredColumnsFormat")}
          </h4>
          <ol className="list-decimal list-inside text-xs text-amber-700 dark:text-amber-300/80 space-y-1">
            <li>{t("createUserFromExcel.colName")}</li>
            <li>{t("createUserFromExcel.colEmail")}</li>
            <li>{t("createUserFromExcel.colPassword")}</li>
            <li>{t("createUserFromExcel.colTestId")}</li>
          </ol>
        </div>
      </div>

      <div className="flex gap-4 pt-4 border-t border-slate-200 dark:border-slate-800">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-white dark:bg-[#0f172a] border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold transition-colors duration-200 text-sm shadow-sm"
          >
            {t("createUserFromExcel.cancelBtn")}
          </button>
        )}
        <button
          onClick={sendData}
          disabled={!file || loading}
          className={`flex-[2] py-2.5 px-4 rounded-lg font-semibold transition-all duration-200 text-center text-sm ${
            file && !loading
              ? "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg shadow-green-500/20"
              : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-600 cursor-not-allowed border border-slate-200 dark:border-slate-700"
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-4 w-4 text-slate-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {t("createUserFromExcel.uploadingBtn")}
            </span>
          ) : (
            t("createUserFromExcel.createUsersBtn")
          )}
        </button>
      </div>
    </div>
  );
}
